// client/src/components/COMMERCANT/MerchantOfferSelection.js

import React, { useMemo, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { CURRENCY_GROUPS, PRICING } from '../../utils/merchantPricing';
import { sanitize } from '../../utils/sanitize';
import { REGEX as PASSWORD_REGEX } from '../../policies/passwordPolicy';
import PasswordField from '../shared/PasswordField';
import LegalAccordion from '../shared/LegalAccordion';
import { BrandMark } from '../shared/BrandLogo';
import { FaLock } from 'react-icons/fa';

// API base (même logique que MerchantSignup)
const RAW_API =
  (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim()) ||
  (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5099');
const API_BASE = RAW_API.replace(/\/+$/, '');
const isCFDirect = /cloudfunctions\.net\/[^/]+$/i.test(API_BASE);
const SIGNUP_ENDPOINT = isCFDirect ? `${API_BASE}/auth/signup` : `${API_BASE}/api/auth/signup`;
const CSRF_ENDPOINT   = isCFDirect ? `${API_BASE}/csrf-token`  : `${API_BASE}/api/csrf-token`;

const sanitizeEmail = (s) => (s || '').normalize('NFKC').replace(/\s+/g, '').toLowerCase();

const normalizePlan = (raw) => {
  if (!raw) return null;
  const val = String(raw).toLowerCase();
  if (val.startsWith('ann')) return 'annual';
  if (val.startsWith('men') || val.startsWith('mon')) return 'monthly';
  if (val === 'annual' || val === 'monthly') return val;
  return null;
};

const getCookie = (name) => {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([$?*|{}\]\\^])/g, '\\$1') + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
};

const SERVICES = [
  'Forfait base',
  'Programme de fidélité',
  'Communication intégrée',
  'Stockage factures & documents',
];

const MerchantOfferSelection = () => {
  const navigate = useNavigate();
  const { code: codeParam } = useParams();


  // Territoire
  const allTerritories = useMemo(() => CURRENCY_GROUPS.flatMap(g => g.territories), []);
  const storedTerritory = (() => { try { return sessionStorage.getItem('merchant_preselected_territory') || ''; } catch { return ''; } })();
  const pendingStep = (() => { try { return sessionStorage.getItem('merchant_signup_step'); } catch { return null; } })();
  const [selectedTerritory, setSelectedTerritory] = useState(storedTerritory || 'France (métropole)');
  const [showTerritoryModal, setShowTerritoryModal] = useState(() => !storedTerritory && pendingStep !== '2');

  const promoCode = useMemo(() => (codeParam || '').trim(), [codeParam]);
  const storedPromo = (() => { try { return sessionStorage.getItem('merchant_preselected_promo') || ''; } catch { return ''; } })();
  const activeCode = storedPromo || promoCode;

  // Détails du code promo (remise affilié) — endpoint public backend (sans auth)
  const [promoDetails, setPromoDetails] = useState(null); // { percentage, monthNumber }
  useEffect(() => {
    if (!activeCode) { setPromoDetails(null); return; }
    const url = `${API_BASE}/api/promo-codes/details?code=${encodeURIComponent(activeCode)}`;
    fetch(url)
      .then(r => r.json())
      .then(data => {
        if (data.found) setPromoDetails({ percentage: data.percentage, monthNumber: data.monthNumber });
        else setPromoDetails(null);
      })
      .catch(() => setPromoDetails(null));
  }, [activeCode]);

  useEffect(() => {
    if (promoCode) {
      try { sessionStorage.setItem('merchant_preselected_promo', promoCode); } catch {}
      navigate('/merchantofferselection', { replace: true, state: { promoCode } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoCode, navigate]);

  const territoryObj = allTerritories.find(t => t.value === selectedTerritory);
  const prices = PRICING[territoryObj?.currencyCode] || PRICING.DEFAULT;

  const applyDiscount = (amount) => promoDetails
    ? amount * (1 - promoDetails.percentage / 100)
    : amount;
  const fmt = (n) => { const v = Number(n); return v % 1 === 0 ? String(v) : v.toFixed(2); };

  const displayMonthly        = showTerritoryModal ? '—' : `${fmt(applyDiscount(prices.monthly))}${prices.currency}`;
  const displayAnnualPerMonth = showTerritoryModal ? '—' : `${fmt(applyDiscount(prices.annual) / 12)}${prices.currency}`;
  const displayAnnualTotal    = showTerritoryModal ? '—' : `${fmt(applyDiscount(prices.annual))}${prices.currency}`;

  // ── Modale inscription ────────────────────────────────────────────────────────
  // Si l'utilisateur rafraîchit après step 1, on rouvre directement au step 2
  const [signupModalOpen, setSignupModalOpen] = useState(pendingStep === '2');
  const [signupPlan, setSignupPlan]           = useState(() => { try { return localStorage.getItem('merchant_preselected_plan') || null; } catch { return null; } });
  const [signupStep, setSignupStep]           = useState(pendingStep === '2' ? 2 : 1);

  // Étape 1 — compte
  const [email, setEmail]                 = useState('');
  const [password, setPassword]           = useState('');
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError]     = useState('');
  // On stocke les credentials pour signer en à l'étape 2 seulement
  // Si refresh après step 1, on relit l'email depuis sessionStorage (password non persisté par sécurité)
  const storedPendingEmail = (() => { try { return sessionStorage.getItem('merchant_pending_email') || null; } catch { return null; } })();
  const [pendingCreds, setPendingCreds]   = useState(
    pendingStep === '2' && storedPendingEmail ? { email: storedPendingEmail, password: null } : null
  );
  // needsReauth : vrai quand le user a refreshé après step 1 → pas de session Firebase, mot de passe inconnu
  const [needsReauth, setNeedsReauth]     = useState(pendingStep === '2' && storedPendingEmail && !auth.currentUser);
  const [reauthPassword, setReauthPassword] = useState('');

  // Étape 2 — infos entreprise
  const [newPrenom, setNewPrenom]         = useState('');
  const [newNom, setNewNom]               = useState('');
  const [newPseudo, setNewPseudo]         = useState('');
  const [newTelephone, setNewTelephone]   = useState('');
  const [newRue, setNewRue]               = useState('');
  const [newVille, setNewVille]           = useState('');
  const [newCodePostal, setNewCodePostal] = useState('');
  const [acceptedCGUV, setAcceptedCGUV]   = useState(false);
  const [infoLoading, setInfoLoading]     = useState(false);
  const [infoError, setInfoError]         = useState('');

  const abortRef  = useRef(null);
  const isPwdValid = PASSWORD_REGEX.test(password || '');

  // Si l'utilisateur rafraîchit la page après la création de compte (step 1),
  // il est déjà authentifié en commercantpending → on saute directement au step 2
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      try {
        const storedPlan = (() => { try { return localStorage.getItem('merchant_preselected_plan'); } catch { return null; } })();
        // Si sessionStorage indique qu'on est au step 2, on fait confiance à la session
        // sans attendre la propagation des custom claims
        const step = (() => { try { return sessionStorage.getItem('merchant_signup_step'); } catch { return null; } })();
        if (step === '2') {
          setSignupPlan(storedPlan || 'monthly');
          setPendingCreds({ email: user.email, password: null });
          setNeedsReauth(false); // session Firebase active, pas besoin de re-saisir le mdp
          setSignupStep(2);
          setSignupModalOpen(true);
          return;
        }
        const tokenResult = await user.getIdTokenResult(true);
        if (tokenResult.claims.role === 'commercantpending') {
          setSignupPlan(storedPlan || 'monthly');
          setPendingCreds({ email: user.email, password: null });
          setSignupStep(2);
          setSignupModalOpen(true);
        }
      } catch {}
    });
    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openSignupModal = (plan) => {
    const cleanPlan = normalizePlan(plan) || 'monthly';
    try { localStorage.setItem('merchant_preselected_plan', cleanPlan); } catch {}
    try { sessionStorage.setItem('merchant_preselected_territory', selectedTerritory); } catch {}
    setSignupPlan(cleanPlan);
    setSignupStep(1);
    setEmail(''); setPassword(''); setSignupError('');
    setSignupModalOpen(true);
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (signupLoading || !isPwdValid || !email.trim()) return;
    setSignupLoading(true);
    setSignupError('');
    const safeEmail = sanitizeEmail(email);
    try {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      try { await fetch(CSRF_ENDPOINT, { credentials: 'include' }); } catch {}
      const xsrf = getCookie('XSRF-TOKEN');
      const resp = await fetch(SIGNUP_ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(xsrf ? { 'X-CSRF-Token': xsrf } : {}),
        },
        body: JSON.stringify({ email: safeEmail, password, role: 'commercantpending' }),
        signal: abortRef.current.signal,
      });
      let data = {};
      try { data = await resp.json(); } catch {}
      if (!resp.ok) throw new Error(data?.error || `Erreur (${resp.status})`);
      // On garde les credentials pour l'étape 2 — on ne se connecte pas encore
      // (évite la redirection automatique de l'AuthContext vers /merchantprocesspaiement)
      // Utiliser l'email retourné par le backend (déjà normalisé par express-validator)
      try { sessionStorage.setItem('merchant_signup_step', '2'); } catch {}
      try { sessionStorage.setItem('merchant_pending_email', data.email || safeEmail); } catch {}
      setPendingCreds({ email: data.email || safeEmail, password });
      setSignupStep(2);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setSignupError(err.message || 'Erreur réseau');
    } finally {
      setSignupLoading(false);
    }
  };

  const isInfoValid = pendingCreds && newPrenom.trim() && newNom.trim() && newPseudo.trim() && newTelephone.trim() && newRue.trim() && newVille.trim() && acceptedCGUV &&
    (!needsReauth || reauthPassword.trim());

  const handleStep2Submit = async () => {
    if (!pendingCreds || !isInfoValid || infoLoading) return;
    setInfoLoading(true);
    setInfoError('');
    try {
      // Connexion Firebase maintenant (juste avant de naviguer pour éviter la redirection auto)
      // Si déjà connecté, on réutilise la session existante
      // Si refresh après step 1 (needsReauth), on utilise le mot de passe re-saisi
      let user = auth.currentUser;
      if (!user) {
        const loginPassword = pendingCreds.password || reauthPassword;
        const result = await signInWithEmailAndPassword(auth, pendingCreds.email, loginPassword);
        user = result.user;
      }
      await user.getIdToken(true); // force refresh pour que Firestore reçoive les custom claims (role)

      const safeAdresse    = [sanitize(newRue), sanitize(newCodePostal), sanitize(newVille)].filter(Boolean).join(', ');
      const currencyCode   = allTerritories.find(t => t.value === selectedTerritory)?.currencyCode || null;
      const effectivePromo = (() => { try { return sessionStorage.getItem('merchant_preselected_promo') || ''; } catch { return ''; } })() || promoCode;

      await setDoc(doc(db, 'users', user.uid), {
        uid:        user.uid,
        role:       'commercantpending',
        prenom:     sanitize(newPrenom),
        nom:        sanitize(newNom),
        pseudo:     sanitize(newPseudo),
        telephone:  sanitize(newTelephone),
        adresse:    safeAdresse,
        rue:        sanitize(newRue),
        ville:      sanitize(newVille),
        codePostal: sanitize(newCodePostal),
        lieu:       selectedTerritory,
        CGV:        'accepted',
        currencyCode,
        pendingPlan: signupPlan,
        ...(effectivePromo ? { codepromo: effectivePromo } : {}),
      }, { merge: true });

      try { sessionStorage.removeItem('merchant_signup_step'); } catch {}
      try { sessionStorage.removeItem('merchant_pending_email'); } catch {}
      // Persister autoStart en localStorage pour survivre au redirect Firebase email verification (nouvel onglet)
      try { localStorage.setItem('merchant_autostart', 'true'); } catch {}

      navigate('/merchantprocesspaiement', {
        state: {
          selectedPlan:      signupPlan,
          selectedTerritory,
          autoStart:         true,
          code:              effectivePromo,
          uid:               user.uid,
        },
      });
    } catch (err) {
      console.error(err);
      if (err?.code === 'auth/invalid-credential' || err?.code === 'auth/wrong-password') {
        setInfoError('Mot de passe incorrect. Veuillez réessayer.');
      } else {
        setInfoError("Erreur lors de l'enregistrement, veuillez réessayer.");
      }
    } finally {
      setInfoLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="pricing-landing">
      <style>{CSS}</style>
      <div className="pricing-landing__halo" />

      {/* Modale sélection territoire */}
      {showTerritoryModal && (
        <div className="territory-modal-backdrop">
          <div className="territory-modal">
            <div className="territory-title">Choisissez votre pays</div>
            <div className="territory-hint">Nous ajustons automatiquement la devise et le prix.</div>
            <select value={selectedTerritory} onChange={e => setSelectedTerritory(e.target.value)}>
              {allTerritories.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <div className="territory-actions">
              <button
                disabled={!selectedTerritory}
                onClick={() => {
                  if (!selectedTerritory) return;
                  try { sessionStorage.setItem('merchant_preselected_territory', selectedTerritory); } catch {}
                  setShowTerritoryModal(false);
                }}
              >
                Valider ce pays
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale inscription (2 étapes) */}
      {signupModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2 className="modal-title">
                {signupStep === 1 ? 'Créer votre compte' : 'Informations de votre entreprise'}
              </h2>
              <p className="modal-hint">
                {signupStep === 1
                  ? `Offre : ${signupPlan === 'annual' ? 'Annuel (1 mois offert)' : 'Mensuel'} · ${selectedTerritory}`
                  : 'Ces informations seront utilisées pour votre compte et vos factures.'}
              </p>
            </div>

            {/* Indicateur d'étapes */}
            <div className="signup-steps">
              <span className={`signup-step-dot ${signupStep >= 1 ? 'done' : ''}`}>1</span>
              <span className={`signup-step-label${signupStep === 1 ? ' is-current' : ''}`}>Compte</span>
              <span className="signup-step-sep">›</span>
              <span className={`signup-step-dot ${signupStep >= 2 ? 'done' : ''}`}>2</span>
              <span className={`signup-step-label${signupStep === 2 ? ' is-current' : ''}`}>Entreprise</span>
            </div>

            {/* Étape 1 — email + mot de passe */}
            {signupStep === 1 && (
              <form onSubmit={handleStep1Submit}>
                <div className="modal-section-label">Connexion</div>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Email"
                  autoComplete="email"
                  disabled={signupLoading}
                  required
                />
                <PasswordField
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Mot de passe"
                  autoComplete="new-password"
                  disabled={signupLoading}
                  required
                  aria-invalid={!!password && !isPwdValid}
                />
                {signupError && <p className="field-error">{signupError}</p>}
                <button
                  type="submit"
                  className="merchant-button"
                  disabled={signupLoading || !isPwdValid || !email.trim()}
                >
                  {signupLoading ? 'Création du compte…' : 'Créer le compte →'}
                </button>
                <button
                  type="button"
                  className="btn back-btn"
                  style={{ marginTop: 8 }}
                  onClick={() => setSignupModalOpen(false)}
                  disabled={signupLoading}
                >
                  Annuler
                </button>
              </form>
            )}

            {/* Étape 2 — infos entreprise */}
            {signupStep === 2 && (
              <div>
                <div className="modal-section-label">Contact de référence</div>
                <div className="modal-row-2">
                  <input type="text" value={newPrenom} onChange={e => setNewPrenom(e.target.value)} placeholder="Prénom" />
                  <input type="text" value={newNom}    onChange={e => setNewNom(e.target.value)}    placeholder="Nom" />
                </div>

                <div className="modal-section-label">Entreprise</div>
                <input type="text"  value={newPseudo}    onChange={e => setNewPseudo(e.target.value)}    placeholder="Nom de l'entreprise" />
                <input type="text"  value={newTelephone} onChange={e => setNewTelephone(e.target.value)} placeholder="Téléphone" />

                <div className="modal-address-block">
                  <div className="modal-section-label" style={{ marginBottom: 10 }}>Adresse de l'entreprise</div>
                  <input type="text" value={newRue} onChange={e => setNewRue(e.target.value)} placeholder="Adresse" style={{ marginBottom: 10 }} />
                  <div className="modal-row-2" style={{ marginBottom: 10 }}>
                    <input type="text" value={newVille}      onChange={e => setNewVille(e.target.value)}      placeholder="Ville"        style={{ marginBottom: 0 }} />
                    <input type="text" value={newCodePostal} onChange={e => setNewCodePostal(e.target.value)} placeholder="Code postal"  style={{ marginBottom: 0 }} />
                  </div>
                  <div className="modal-territory-locked">{selectedTerritory}</div>
                  <div className="modal-territory-warn">
                    Le pays détermine votre devise monétaire et ne pourra pas être modifié ultérieurement.
                  </div>
                </div>

                {needsReauth && (
                  <div style={{ marginBottom: 12 }}>
                    <div className="modal-section-label">Confirmez votre mot de passe</div>
                    <input
                      type="password"
                      value={reauthPassword}
                      onChange={e => setReauthPassword(e.target.value)}
                      placeholder="Mot de passe"
                      autoComplete="current-password"
                    />
                  </div>
                )}

                <LegalAccordion docId="cgv" checked={acceptedCGUV} onChange={setAcceptedCGUV} />

                {infoError && <p className="field-error">{infoError}</p>}
                <button className="merchant-button" onClick={handleStep2Submit} disabled={!isInfoValid || infoLoading}>
                  {infoLoading ? 'Enregistrement…' : 'Enregistrer et continuer →'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="pricing-brand"><BrandMark /></div>

      {/* En-tête */}
      <header className="pricing-hero">
        <div className="pricing-hero__eyebrow">Bienvenue chez Ecobill</div>
        <h1>Choisissez votre formule</h1>
        <p>Comparez nos abonnements puis créez votre compte commerçant en quelques secondes.</p>
        {(storedPromo || promoCode) && (
          <div className="pricing-hero__code">Code partenaire : {storedPromo || promoCode}</div>
        )}
      </header>

      {/* Cartes forfaits */}
      <section className="pricing-grid" aria-label="Choix des formules" style={{ marginTop: 28 }}>
        <div className="plan-grid" style={{ marginTop: 0 }}>

          {/* Mensuel */}
          <div className="plan-card" aria-label="Formule mensuelle">
            {promoDetails && <div className="promo-badge">-{promoDetails.percentage}% partenaire</div>}
            <div className="plan-title">Mensuel</div>
            <div className="plan-sub">Idéal pour démarrer, sans engagement</div>
            <div className="plan-price">
              <span className="price-amount">{displayMonthly}</span>
              <span className="price-period">{showTerritoryModal ? '' : ' / mois'}</span>
            </div>
            {promoDetails && !showTerritoryModal && (
              <>
                {promoDetails.monthNumber > 0 && (
                  <div className="promo-duration">Remise sur {promoDetails.monthNumber} mois</div>
                )}
                <div className="promo-then">Puis {prices.monthly}{prices.currency}/mois</div>
              </>
            )}
            <div className="plan-features">
              <ul>
                {SERVICES.map(s => <li key={s}>{s}</li>)}
                <li>Support prioritaire 7/7</li>
                <li>Sans engagement</li>
              </ul>
            </div>
            <div className="plan-cta">
              <button className="btn" onClick={() => openSignupModal('monthly')} disabled={showTerritoryModal}>
                Choisir ce forfait
              </button>
            </div>
          </div>

          {/* Annuel */}
          <div className="plan-card plan-card--featured" aria-label="Formule annuelle">
            {promoDetails
              ? <div className="plan-badge">-{promoDetails.percentage}% partenaire 🎁</div>
              : <div className="plan-badge">1 mois offert 🎁</div>
            }
            <div className="plan-title">Annuel</div>
            <div className="plan-sub">1 mois offert pour un engagement de 12 mois</div>
            <div className="plan-price">
              <span className="price-amount">{displayAnnualTotal}</span>
              <span className="price-period">{showTerritoryModal ? '' : ' / an'}</span>
              {!showTerritoryModal && (
                <span className="plan-equiv">(soit {displayAnnualPerMonth}/mois)</span>
              )}
            </div>
            {promoDetails && !showTerritoryModal && (
              <>
                <div className="promo-duration">Remisé pendant 1 an</div>
                <div className="promo-then">Puis {prices.annual}{prices.currency}/an</div>
              </>
            )}
            <div className="plan-features">
              <ul>
                {SERVICES.map(s => <li key={s}>{s}</li>)}
                <li>Support prioritaire 7/7</li>
                <li>Engagement de 1 an</li>
              </ul>
            </div>
            <div className="plan-cta">
              <button className="btn btn-primary" onClick={() => openSignupModal('annual')} disabled={showTerritoryModal}>
                Choisir ce forfait
              </button>
            </div>
          </div>

        </div>
      </section>

      <div className="pricing-trust">
        <span><FaLock aria-hidden="true" /> Paiement sécurisé par Stripe</span>
      </div>

      <footer className="pricing-footer">
        <span>Déjà client ?</span>
        <Link to="/logingeneral" className="pricing-footer__link">Se connecter</Link>
      </footer>
    </div>
  );
};

const CSS = `
  .pricing-landing {
    position: relative; min-height: 100vh; min-height: 100dvh;
    background:
      radial-gradient(900px 500px at 15% -10%, rgba(39, 76, 116, 0.12), transparent 60%),
      radial-gradient(700px 400px at 100% 110%, rgba(20, 48, 40, 0.08), transparent 60%),
      var(--eb-bg);
    display: flex; flex-direction: column; align-items: center;
    padding: 40px 16px 64px;
    color: var(--eb-text);
  }
  .pricing-landing__halo { display: none; }
  .pricing-brand { margin-bottom: 28px; z-index: 1; }

  /* Hero */
  .pricing-hero { text-align: center; max-width: 720px; z-index: 1; }
  .pricing-hero__eyebrow {
    display: inline-flex; align-items: center; gap: 8px; padding: 4px 12px;
    border-radius: var(--eb-radius-pill); background: var(--eb-brand-50); border: 1px solid var(--eb-brand-100);
    color: var(--eb-brand); font-weight: 600; font-size: 12.5px; letter-spacing: .02em;
  }
  .pricing-hero h1 { margin: 16px 0 8px; font-size: clamp(26px, 4vw, 36px); font-weight: 800; letter-spacing: -0.025em; color: var(--eb-text); }
  .pricing-hero p  { margin: 0 0 12px; color: var(--eb-muted); font-size: 16px; }
  .pricing-hero__code {
    display: inline-flex; align-items: center; gap: 6px; margin-top: 4px; padding: 6px 12px; border-radius: var(--eb-radius-pill);
    background: var(--eb-success-bg); border: 1px solid #c6f0da; color: var(--eb-success); font-weight: 600; font-size: 13.5px;
  }

  /* Grille + cartes */
  .pricing-grid { width: 100%; max-width: 900px; z-index: 1; }
  .plan-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 10px; align-items: stretch; }
  .plan-card {
    position: relative; border-radius: var(--eb-radius-xl); padding: 28px 24px 24px; background: var(--eb-surface);
    border: 1px solid var(--eb-border); display: flex; flex-direction: column;
    transition: box-shadow .2s ease, border-color .2s ease;
    box-shadow: var(--eb-shadow-sm);
  }
  .plan-card:hover { box-shadow: var(--eb-shadow-md); border-color: var(--eb-border-strong); }
  .plan-card--featured { border: 2px solid var(--eb-brand); box-shadow: var(--eb-shadow-md); }
  .plan-card--featured:hover { border-color: var(--eb-brand); }
  .plan-badge, .promo-badge {
    position: absolute; top: -13px; left: 50%; transform: translateX(-50%);
    background: var(--eb-brand); color: #fff; font-size: 12.5px; font-weight: 600;
    padding: 4px 12px; border-radius: var(--eb-radius-pill); white-space: nowrap; box-shadow: var(--eb-shadow-xs);
  }
  .promo-badge { background: var(--eb-warning); }
  .promo-duration { text-align: center; font-size: 13px; font-weight: 600; color: var(--eb-warning); margin-top: 4px; margin-bottom: 2px; }
  .promo-then { text-align: center; font-size: 13.5px; color: var(--eb-text-2); font-weight: 500; margin-bottom: 6px; }
  .plan-title { text-align: center; font-weight: 700; font-size: 20px; color: var(--eb-text); letter-spacing: -0.015em; }
  .plan-sub { text-align: center; color: var(--eb-muted); font-size: 14px; margin: 6px 0; min-height: 40px; }
  .plan-price { text-align: center; margin: 12px 0 4px; padding-bottom: 16px; border-bottom: 1px solid var(--eb-border); }
  .price-amount { font-size: 36px; font-weight: 800; letter-spacing: -0.03em; color: var(--eb-text); font-variant-numeric: tabular-nums; }
  .price-period { font-size: 14px; color: var(--eb-muted); }
  .plan-total { text-align: center; color: var(--eb-text-2); font-weight: 600; font-size: 14px; margin-bottom: 8px; }
  .plan-equiv { display: block; color: var(--eb-muted); font-size: 13.5px; font-weight: 500; margin-top: 4px; }
  .plan-features { margin: 16px 0 4px; font-size: 14px; color: var(--eb-text-2); flex-grow: 1; }
  .plan-features ul { list-style: none; padding: 0; margin: 0; }
  .plan-features li { display: flex; align-items: flex-start; margin-bottom: 10px; line-height: 1.4; }
  .plan-features li::before {
    content: '✓'; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
    width: 18px; height: 18px; margin-right: 10px; margin-top: 1px; border-radius: 50%;
    background: var(--eb-success-bg); color: var(--eb-success); font-size: 11px; font-weight: 800;
  }
  .plan-cta { margin-top: 16px; }
  .btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    min-height: 48px; padding: 0 18px; width: 100%;
    border: 1px solid var(--eb-border-strong); background: var(--eb-surface); color: var(--eb-text-2);
    border-radius: var(--eb-radius); font-weight: 600; font-size: 15px; cursor: pointer; box-shadow: var(--eb-shadow-xs);
    transition: background-color .15s ease, border-color .15s ease, color .15s ease;
  }
  .btn:hover:not(:disabled) { background: var(--eb-surface-2); color: var(--eb-text); }
  .btn:focus-visible { outline: none; box-shadow: var(--eb-ring); }
  .btn:disabled { opacity: .55; cursor: not-allowed; }
  .btn-primary { background: var(--eb-brand); border-color: var(--eb-brand); color: #fff; }
  .btn-primary:hover:not(:disabled) { background: var(--eb-brand-700); border-color: var(--eb-brand-700); color: #fff; }
  .back-btn { background: transparent; border-color: transparent; box-shadow: none; color: var(--eb-muted); }
  .back-btn:hover:not(:disabled) { background: var(--eb-surface-2); }

  .pricing-trust { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px 20px; margin-top: 24px; color: var(--eb-muted); font-size: 13.5px; z-index: 1; }
  .pricing-trust span { display: inline-flex; align-items: center; gap: 6px; }

  /* Footer */
  .pricing-footer { margin-top: 20px; display: flex; align-items: center; gap: 8px; color: var(--eb-muted); font-size: 14px; z-index: 1; }
  .pricing-footer__link { font-weight: 600; color: var(--eb-brand); text-decoration: none; }
  .pricing-footer__link:hover { text-decoration: underline; }

  /* Modale territoire */
  .territory-modal-backdrop {
    position: fixed; inset: 0; background: rgba(15, 35, 56, .55);
    backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
    display: flex; align-items: center; justify-content: center;
    z-index: 9999; padding: 16px;
  }
  .territory-modal {
    background: var(--eb-surface); border-radius: var(--eb-radius-xl); padding: 24px;
    width: 100%; max-width: 440px; box-shadow: var(--eb-shadow-lg); border: 1px solid var(--eb-border);
  }
  .territory-title { font-weight: 700; font-size: 18px; color: var(--eb-text); margin-bottom: 4px; }
  .territory-hint  { color: var(--eb-muted); font-size: 14px; margin-bottom: 16px; }
  .territory-modal select {
    width: 100%; min-height: 46px; padding: 0 12px; font-size: 15px; font-weight: 500; margin-bottom: 20px;
  }
  .territory-actions { display: flex; justify-content: flex-end; }
  .territory-modal button {
    min-height: 46px; padding: 0 20px; border: 1px solid var(--eb-brand);
    background: var(--eb-brand); color: #fff; font-weight: 600; font-size: 15px;
    border-radius: var(--eb-radius-sm); box-shadow: var(--eb-shadow-xs); cursor: pointer;
  }
  .territory-modal button:hover:not(:disabled) { background: var(--eb-brand-700); }
  .territory-modal button:disabled { opacity: .55; cursor: not-allowed; }

  /* Modale inscription */
  .modal-overlay {
    position: fixed; inset: 0; background: rgba(15, 35, 56, .55);
    backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
    display: flex; align-items: center; justify-content: center;
    z-index: 9999; padding: 16px; overflow-y: auto;
  }
  .modal-card {
    background: var(--eb-surface); border-radius: var(--eb-radius-xl); padding: 28px 24px 24px;
    max-width: 480px; width: 100%; box-shadow: var(--eb-shadow-lg); margin: auto;
  }
  .modal-header { text-align: center; margin-bottom: 18px; }
  .modal-title  { font-size: 20px; font-weight: 700; letter-spacing: -0.015em; color: var(--eb-text); margin: 0 0 6px; text-align: center; }
  .modal-hint   { font-size: 14px; color: var(--eb-muted); margin: 0; }

  /* Indicateur d'étapes */
  .signup-steps { display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 22px; }
  .signup-step-dot {
    width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    background: var(--eb-border); color: var(--eb-muted); font-weight: 700; font-size: 12.5px; transition: background .2s, color .2s;
  }
  .signup-step-dot.done { background: var(--eb-brand); color: #fff; }
  .signup-step-label { font-weight: 600; font-size: 13px; color: var(--eb-subtle); }
  .signup-step-label.is-current { color: var(--eb-brand); }
  .signup-step-sep { color: var(--eb-border-strong); font-size: 16px; }

  /* Champs */
  .modal-section-label {
    font-size: 12.5px; font-weight: 600; color: var(--eb-muted);
    text-transform: uppercase; letter-spacing: .05em; margin-bottom: 8px; margin-top: 4px;
  }
  .modal-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; }
  .modal-row-2 > * { min-width: 0; }
  .modal-card input:not([type='checkbox']):not([type='radio']) {
    width: 100%; min-height: 44px; padding: 0 12px; margin-bottom: 12px; font-size: 15px;
    box-shadow: var(--eb-shadow-xs);
  }
  .modal-row-2 input:not([type='checkbox']) { margin-bottom: 0; }
  .modal-address-block {
    background: var(--eb-surface-2); border: 1px solid var(--eb-border); border-radius: var(--eb-radius);
    padding: 14px 16px; margin-bottom: 16px;
  }
  .modal-address-block input { margin-bottom: 0; }
  .modal-territory-locked {
    background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-sm);
    padding: 10px 12px; font-size: 14px; font-weight: 600; color: var(--eb-text-2); margin-bottom: 10px;
  }
  .modal-territory-warn {
    font-size: 13px; color: var(--eb-warning); background: var(--eb-warning-bg); border: 1px solid #fde3b0;
    border-radius: var(--eb-radius-sm); padding: 8px 10px; font-weight: 500; line-height: 1.45;
  }

  /* Checkbox & boutons */
  .checkbox-area { display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 16px; font-size: 14px; color: var(--eb-text-2); }
  .checkbox-area input[type="checkbox"] { flex-shrink: 0; width: 16px; height: 16px; cursor: pointer; margin: 0; }
  .checkbox-area label { margin: 0; cursor: pointer; }
  .link-cguv { color: var(--eb-brand); font-weight: 600; text-decoration: underline; }
  .merchant-button {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    background: var(--eb-brand); color: #fff; border: 1px solid transparent; border-radius: var(--eb-radius);
    min-height: 50px; padding: 0 20px; font-weight: 600; cursor: pointer; width: 100%;
    font-size: 15.5px; box-shadow: var(--eb-shadow-xs); transition: background-color .15s ease;
  }
  .merchant-button:disabled { opacity: .55; cursor: not-allowed; }
  .merchant-button:hover:not(:disabled) { background: var(--eb-brand-700); }
  .merchant-button:focus-visible { outline: none; box-shadow: var(--eb-ring); }
  .field-error {
    color: #912018; background: var(--eb-danger-bg); border: 1px solid #fbd0cb;
    border-radius: var(--eb-radius-sm); padding: 10px 12px; margin: 0 0 12px; font-size: 14px;
  }

  @media (max-height: 700px), (max-width: 480px) {
    .modal-overlay { align-items: flex-start; padding: 12px 12px 40px; }
    .modal-card    { padding: 22px 16px; border-radius: var(--eb-radius-lg); margin: 0 auto; }
    .modal-header  { margin-bottom: 14px; }
    .modal-title   { font-size: 18px; }
    .modal-address-block { padding: 12px; }
  }

  /* Mobile */
  @media (max-width: 640px) {
    .pricing-landing { padding: 28px 16px 48px; }
    .pricing-brand { margin-bottom: 20px; }
    .plan-grid { grid-template-columns: 1fr; gap: 24px; max-width: 440px; margin-left: auto; margin-right: auto; }
    .plan-card { padding: 24px 18px 18px; }
    .plan-sub { min-height: 0; }
    .price-amount { font-size: 32px; }
    .modal-row-2 { gap: 8px; }
  }
`;

export default MerchantOfferSelection;
